import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { listChannels } from '../../api/channels';
import { contactsExportUrl } from '../../api/contacts';
import { listMembers } from '../../api/team';
import {
  FUNNEL_STAGE_LABELS,
  FUNNEL_STAGES,
  KOTBOT_BADGE_VALUES,
  kotbotBadgeLabel,
  kotbotBadgeTone,
  type ContactDto,
  type ContactListFilters,
} from '../../api/types';
import { useBulkPatchContacts, useContactList, useContactSources } from '../../hooks/use-contacts';
import { Avatar } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/toast';

export interface ContactsTableProps {
  filters: ContactListFilters;
  onFiltersChange: (next: ContactListFilters) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function contactName(c: ContactDto): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ');
  return name || `VK ID ${c.vkUserId}`;
}

export function ContactsTable({ filters, onFiltersChange, selectedId, onSelect }: ContactsTableProps) {
  const [search, setSearch] = useState(filters.q ?? '');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [bulkOwner, setBulkOwner] = useState('');
  const [bulkTag, setBulkTag] = useState('');
  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels });
  const members = useQuery({ queryKey: ['team', 'members'], queryFn: listMembers });
  const sources = useContactSources();
  const { data, isLoading, isError } = useContactList(filters);
  const bulkPatch = useBulkPatchContacts();
  const toast = useToast();

  function set<K extends keyof ContactListFilters>(key: K, value: ContactListFilters[K]) {
    onFiltersChange({ ...filters, [key]: value || undefined, cursor: undefined });
  }

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    set('q', search || undefined);
  }

  function toggleChecked(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    const items = data?.items ?? [];
    setChecked((prev) => (prev.size === items.length ? new Set() : new Set(items.map((c) => c.id))));
  }

  function exportUrl(format: 'csv' | 'xlsx', selectedOnly: boolean) {
    return contactsExportUrl(format, filters, selectedOnly ? [...checked] : undefined);
  }

  async function applyBulkOwner() {
    if (checked.size === 0) return;
    const res = await bulkPatch.mutateAsync({ ids: [...checked], body: { owner_user_id: bulkOwner || null } });
    if (res.failed > 0) toast.push(`Не удалось сменить владельца у ${res.failed} из ${res.total}`);
    setChecked(new Set());
  }

  async function applyBulkTag() {
    if (checked.size === 0 || !bulkTag.trim()) return;
    const items = data?.items.filter((c) => checked.has(c.id)) ?? [];
    await Promise.allSettled(
      items.map((c) =>
        bulkPatch.mutateAsync({ ids: [c.id], body: { tags: c.tags.includes(bulkTag.trim()) ? c.tags : [...c.tags, bulkTag.trim()] } }),
      ),
    );
    setBulkTag('');
    setChecked(new Set());
  }

  return (
    <div className="flex h-full flex-col">
      <form onSubmit={submitSearch} className="flex flex-wrap items-center gap-2 border-b border-border p-3">
        <Input
          placeholder="Поиск по имени, VK ID…"
          className="w-56"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Поиск контактов"
        />
        <Select aria-label="Стадия" value={filters.stage ?? ''} onChange={(e) => set('stage', e.target.value)}>
          <option value="">Любая стадия</option>
          {FUNNEL_STAGES.map((s) => (
            <option key={s} value={s}>
              {FUNNEL_STAGE_LABELS[s]}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Канал"
          value={filters.channel_id ?? ''}
          onChange={(e) => set('channel_id', e.target.value)}
        >
          <option value="">Все каналы</option>
          {(channels.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </Select>
        <Select aria-label="Источник" value={filters.source_id ?? ''} onChange={(e) => set('source_id', e.target.value)}>
          <option value="">Любой источник</option>
          {(sources.data?.sources ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </Select>
        <Select aria-label="Kotbot" value={filters.kotbot ?? ''} onChange={(e) => set('kotbot', e.target.value)}>
          <option value="">Любой kotbot-статус</option>
          {KOTBOT_BADGE_VALUES.map((v) => (
            <option key={v} value={v}>
              {kotbotBadgeLabel(v)}
            </option>
          ))}
        </Select>
        <Select aria-label="Владелец" value={filters.owner ?? ''} onChange={(e) => set('owner', e.target.value)}>
          <option value="">Любой владелец</option>
          {(members.data?.members ?? []).map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name || m.email}
            </option>
          ))}
        </Select>
        <Input
          placeholder="Тег"
          className="w-32"
          value={filters.tag ?? ''}
          onChange={(e) => set('tag', e.target.value)}
          aria-label="Тег"
        />
        <Button type="submit" variant="secondary" size="sm">
          Применить
        </Button>
        <div className="ml-auto flex gap-1">
          <Button variant="secondary" size="sm" onClick={() => (window.location.href = exportUrl('csv', false))}>
            Экспорт CSV
          </Button>
          <Button variant="secondary" size="sm" onClick={() => (window.location.href = exportUrl('xlsx', false))}>
            Экспорт XLSX
          </Button>
        </div>
      </form>

      {checked.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-primary/5 p-2 text-xs">
          <span className="font-medium">Выбрано: {checked.size}</span>
          <Select aria-label="Новый владелец" value={bulkOwner} onChange={(e) => setBulkOwner(e.target.value)} className="h-8">
            <option value="">Без владельца</option>
            {(members.data?.members ?? []).map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name || m.email}
              </option>
            ))}
          </Select>
          <Button size="sm" variant="secondary" onClick={() => void applyBulkOwner()}>
            Сменить владельца
          </Button>
          <Input
            placeholder="Новый тег"
            className="h-8 w-28"
            aria-label="Новый тег для выбранных"
            value={bulkTag}
            onChange={(e) => setBulkTag(e.target.value)}
          />
          <Button size="sm" variant="secondary" onClick={() => void applyBulkTag()} disabled={!bulkTag.trim()}>
            Добавить тег
          </Button>
          <Button size="sm" variant="secondary" onClick={() => (window.location.href = exportUrl('csv', true))}>
            Экспорт выбранных
          </Button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}
        {isError && <div className="p-4 text-sm text-red-600">Не удалось загрузить контакты.</div>}
        {!isLoading && !isError && (data?.items.length ?? 0) === 0 && (
          <div className="flex h-full items-center justify-center p-8 text-sm text-muted-fg">Контакты не найдены</div>
        )}
        {!isLoading && (data?.items.length ?? 0) > 0 && (
          // Fixed min-width + the wrapper's own scroll (above) trades a wide table for a
          // horizontal scrollbar on narrow viewports, instead of every column's text
          // wrapping letter-by-letter as it gets squeezed toward zero width.
          <table className="w-full min-w-[880px] text-sm">
            <thead className="sticky top-0 bg-muted/60 text-left text-xs uppercase text-muted-fg">
              <tr>
                <th className="px-2 py-2">
                  <input
                    type="checkbox"
                    aria-label="Выбрать все"
                    checked={checked.size > 0 && checked.size === (data?.items.length ?? 0)}
                    onChange={toggleAll}
                  />
                </th>
                <th className="px-3 py-2" />
                <th className="px-3 py-2">Имя</th>
                <th className="px-3 py-2">VK ID</th>
                <th className="px-3 py-2">Источник</th>
                <th className="px-3 py-2">Стадия</th>
                <th className="px-3 py-2">Теги</th>
                <th className="px-3 py-2">Kotbot</th>
                <th className="px-3 py-2">Владелец</th>
                <th className="px-3 py-2">Обновлён</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((c) => {
                const owner = members.data?.members.find((m) => m.userId === c.ownerUserId);
                return (
                  <tr
                    key={c.id}
                    onClick={() => onSelect(c.id)}
                    className={`cursor-pointer border-b border-border hover:bg-muted/40 ${
                      selectedId === c.id ? 'bg-primary/10' : ''
                    }`}
                  >
                    <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Выбрать ${contactName(c)}`}
                        checked={checked.has(c.id)}
                        onChange={() => toggleChecked(c.id)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Avatar name={contactName(c)} photoUrl={c.photoUrl} size="sm" />
                    </td>
                    <td className="px-3 py-2 font-medium">{contactName(c)}</td>
                    <td className="px-3 py-2 text-xs text-muted-fg">{c.vkUserId}</td>
                    <td className="px-3 py-2 text-xs text-muted-fg">{c.sourceTitle ?? '—'}</td>
                    <td className="px-3 py-2">
                      <Badge tone="info">
                        {FUNNEL_STAGE_LABELS[c.funnelStage as keyof typeof FUNNEL_STAGE_LABELS] ?? c.funnelStage}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {c.tags.slice(0, 3).map((t) => (
                          <Badge key={t}>{t}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={kotbotBadgeTone(c.kotbotBadge)}>{kotbotBadgeLabel(c.kotbotBadge)}</Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-fg">{owner?.name || owner?.email || '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-fg">{new Date(c.updatedAt).toLocaleString('ru-RU')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-border p-2 text-xs text-muted-fg">
        <span>{data?.items.length ?? 0} контактов на странице</span>
        <Button
          variant="secondary"
          size="sm"
          disabled={!data?.nextCursor}
          onClick={() => onFiltersChange({ ...filters, cursor: data?.nextCursor ?? undefined })}
        >
          Ещё
        </Button>
      </div>
    </div>
  );
}
